import React, { useState, useEffect, useRef } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { auth, db } from './../../firebase/firebaseConfig';
import { addDoc, collection, query, where, getDocs, deleteDoc, doc } from 'firebase/firestore';
import PageTitle from '../includes/PageTitle';
import './../css/Profile.css';
import { useNavigate, useLocation } from "react-router-dom";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import Select from 'react-select';
import { Country, State, City } from 'country-state-city';

export default function Profile() {
  const [user, setUser] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [company, setCompany] = useState('');
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [country, setCountry] = useState(null);
  const [state, setState] = useState(null);
  const [city, setCity] = useState(null);
  const [postalCode, setPostalCode] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const location = useLocation();
  const successMessage = location.state?.successMessage;

  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(getAuth(), (user) => {
      if (user) {
        setUser(user);
        fetchAddresses(user.uid); // Fetch addresses when user is authenticated
      } else {
        setUser(null);
      }
    });
    return () => unsubscribe();
  }, []);

  const validateName = (name) => /^[A-Za-z]+$/.test(name);
  const validatePostalCode = (code) => /^[0-9]+$/.test(code);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    switch (id) {
      case 'firstName':
        if (validateName(value)) setFirstName(value);
        break;
      case 'lastName':
        if (validateName(value)) setLastName(value);
        break;
      case 'company':
        setCompany(value);
        break;
      case 'address1':
        setAddress1(value);
        break;
      case 'address2':
        setAddress2(value);
        break;
      case 'postalCode':
        if (validatePostalCode(value)) setPostalCode(value);
        break;
      default:
        break;
    }
  };

  const handleCountryChange = (selectedOption) => {
    setCountry(selectedOption);
    setState(null); // Reset state when country changes
    setCity(null);  // Reset city when country changes
  };

  const handleStateChange = (selectedOption) => {
    setState(selectedOption);
    setCity(null);  // Reset city when state changes
  };

  const handleCityChange = (selectedOption) => {
    setCity(selectedOption);
  };

  const validateForm = () => {
    return firstName && lastName && address1 && country && state && city && postalCode && phoneNumber;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      setErrorMessage('Please fill in all required fields.');
      return;
    }
    
    setLoading(true);
    try {
      if (!user) {
        throw new Error('User is not authenticated.');
      }

      await addDoc(collection(db, "addresses"), {
        userId: user.uid,
        firstName,
        lastName,
        company: company || '',
        address1,
        address2: address2 || '',
        country: country ? country.label : '',
        state: state ? state.label : '',
        city: city ? city.label : '',
        postalCode,
        phoneNumber,
      });

      navigate('/profile', { state: { successMessage: 'Address added successfully!' } });
      // Reset form fields after successful submission
      setFirstName('');
      setLastName('');
      setCompany('');
      setAddress1('');
      setAddress2('');
      setCountry(null);
      setState(null);
      setCity(null);
      setPostalCode('');
      setPhoneNumber('');
    } catch (error) {
      console.error("Error adding address:", error);
      setErrorMessage(error.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const countryOptions = Country.getAllCountries().map(country => ({ value: country.isoCode, label: country.name }));
  const stateOptions = country ? State.getStatesOfCountry(country.value).map(state => ({ value: state.isoCode, label: state.name })) : [];
  const cityOptions = state ? City.getCitiesOfState(country.value, state.value).map(city => ({ value: city.name, label: city.name })) : [];

  if (!user) {
    navigate("/sign-in");
  }


  const fetchAddresses = async (userId) => {
    setLoading(true);
    try {
      const q = query(collection(db, "addresses"), where("userId", "==", userId));
      const querySnapshot = await getDocs(q);
      const addressesData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAddresses(addressesData);
    } catch (error) {
      console.error("Error fetching addresses:", error);
      setErrorMessage('An error occurred while fetching addresses.');
    } finally {
      setLoading(false);
    }
  };

  // deleting address
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const deleteAddressModalRef = useRef(null); // Reference to the delete modal

  const handleDeleteClick = (addressId) => {
    setSelectedAddressId(addressId);
    if (deleteAddressModalRef.current) {
      const myModal = new window.bootstrap.Modal(deleteAddressModalRef.current);
      myModal.show();
    }
  };

  const handleDeleteAddress = async () => {
    if (!selectedAddressId) return;

    setLoading(true);
    try {
      await deleteDoc(doc(db, "addresses", selectedAddressId));
      setAddresses(addresses.filter(address => address.id !== selectedAddressId));
      navigate('/profile', { state: { successMessage: 'Address deleted successfully!' } });
    } catch (error) {
      console.error("Error deleting address:", error);
      setErrorMessage('An error occurred while deleting the address.');
    } finally {
      setLoading(false);
      if (deleteAddressModalRef.current) {
        const myModal = new window.bootstrap.Modal(deleteAddressModalRef.current);
        myModal.hide();
      }
    }
  };
  
  

 

  return (
    <div style={{ marginTop: "25vh" }}>
      <PageTitle title="Profile - ALMOSTINVISIBLE" />
      
      <div className="container px-4">
      {successMessage && <div className="alert alert-success" role="alert">{successMessage}</div>}
        <div className="row gx-5">
          <div className="col-md-3">
            <ul className="nav nav-tabs" role="tablist">
              <li className="nav-item">
                <a className="nav-link unbounded active" data-bs-toggle="tab" href="#account">Account</a>
              </li>
              {/*
              <li className="nav-item">
                <a className="nav-link" data-bs-toggle="tab" href="#orders">Orders</a>
              </li>*/}
              <li className="nav-item">
                <a className="nav-link" data-bs-toggle="tab" href="#addresses">Addresses</a>
              </li>
            </ul>
          </div>
          <div className="col-md-9">
            <div className="tab-content pt-5">
              <div id="account" className="container tab-pane active">
                <h3 className='unbounded'>Account</h3>
                <hr />
                {user ? (
                  <>
                    <h5>Name: {user.displayName}</h5>
                    <h5>Email: {user.email}</h5>
                  </>
                ) : (
                  <p>Loading user information...</p>
                )}
              </div>
              {/*
              <div id="orders" className="container tab-pane fade">
                <h3 className='unbounded'>Orders</h3>
                <hr />
                <p>You mi</p>
              </div>*/}
              <div id="addresses" className="container tab-pane fade">
                <h3 className='unbounded'>Addresses</h3>
                
                <hr />
                {loading && <p>Loading addresses...</p>}
                {!loading && addresses.length === 0 && (
                  <div className="d-flex justify-content-center">
                    <button type="button" className="black-button" data-bs-toggle="modal" data-bs-target="#addNewAddress">
                      <svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 1024 1024" version="1.1" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg">
                        <path d="M474 152m8 0l60 0q8 0 8 8l0 704q0 8-8 8l-60 0q-8 0-8-8l0-704q0-8 8-8Z"></path>
                        <path d="M168 474m8 0l672 0q8 0 8 8l0 60q0 8-8 8l-672 0q-8 0-8-8l0-60q0-8 8-8Z"></path>
                      </svg>
                      Add New Address
                    </button>
                  </div>
                )}
                {!loading && addresses.length > 0 && (
                  <div className="row">
                    {addresses.map((address,index) => (
                      <div key={address.id} className="col-md-4 mb-4">
                        <div className="card">
                          <div className="card-body">
                            <h5 className="card-title fw-bold">{index + 1}. {address.firstName} {address.lastName}</h5>
                            <p className="card-text">{address.address1} {address.address2}</p>
                            <p className="card-text">{address.city}, {address.state}, {address.country}, {address.postalCode}</p>
                            <p className="card-text">{address.phoneNumber}</p>
                            <button 
                                className="red-button btn-sm float-end"
                                onClick={() => handleDeleteClick(address.id)}
                              >
                                Delete Address
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add New Address Modal */}
      <div className="modal fade" id="addNewAddress" tabIndex="-1" aria-labelledby="addNewAddressLabel" aria-hidden="true">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="addNewAddressLabel">Add New Address</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body">
              {errorMessage && <div className="alert alert-danger" role="alert">{errorMessage}</div>}
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label htmlFor="firstName" className="form-label">First Name</label>
                  <input type="text" className="form-control" id="firstName" value={firstName} onChange={handleInputChange} />
                </div>
                <div className="mb-3">
                  <label htmlFor="lastName" className="form-label">Last Name</label>
                  <input type="text" className="form-control" id="lastName" value={lastName} onChange={handleInputChange} />
                </div>
                <div className="mb-3">
                  <label htmlFor="company" className="form-label">Company (Optional)</label>
                  <input type="text" className="form-control" id="company" value={company} onChange={handleInputChange} />
                </div>
                <div className="mb-3">
                  <label htmlFor="address1" className="form-label">Address 1</label>
                  <input type="text" className="form-control" id="address1" value={address1} onChange={handleInputChange} />
                </div>
                <div className="mb-3">
                  <label htmlFor="address2" className="form-label">Address 2 (Optional)</label>
                  <input type="text" className="form-control" id="address2" value={address2} onChange={handleInputChange} />
                </div>
                <div className="mb-3">
                  <label htmlFor="country" className="form-label">Country</label>
                  <Select id="country" options={countryOptions} value={country} onChange={handleCountryChange} />
                </div>
                <div className="mb-3">
                  <label htmlFor="state" className="form-label">State</label>
                  <Select id="state" options={stateOptions} value={state} onChange={handleStateChange} />
                </div>
                <div className="mb-3">
                  <label htmlFor="city" className="form-label">City</label>
                  <Select id="city" options={cityOptions} value={city} onChange={handleCityChange} />
                </div>
                <div className="mb-3">
                  <label htmlFor="postalCode" className="form-label">Postal Code</label>
                  <input type="text" className="form-control" id="postalCode" value={postalCode} onChange={handleInputChange} />
                </div>
                <div className="mb-3">
                  <label htmlFor="phoneNumber" className="form-label">Phone Number</label>
                  <PhoneInput
                    country={'us'}
                    value={phoneNumber}
                    onChange={setPhoneNumber}
                    inputProps={{ name: 'phoneNumber', required: true }}
                  />
                </div>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : 'Save Address'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Address Modal */}
      <div className="modal fade" id="deleteAddress" tabIndex="-1" aria-labelledby="deleteAddressLabel" aria-hidden="true" ref={deleteAddressModalRef}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="deleteAddressLabel">Confirm Deletion</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body">
              Are you sure you want to delete this address?
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
              <button type="button" className="btn btn-danger" onClick={handleDeleteAddress}>Delete</button>
            </div>
          </div>
        </div>
      </div>
    
    </div>
  );
}
